import { useState, useEffect, useCallback } from 'react';
import { Home, LayoutGrid, Info, Instagram, Facebook, Send, X, FileText, Upload, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import Cropper from 'react-easy-crop';

// დამხმარე ფუნქცია მრგვალი ფოტოს ამოსაჭრელად (განახლებული - ავტომატური დაპატარავებით)
const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => (image.onload = resolve));
  const canvas = document.createElement('canvas');
  
  // ვსაზღვრავთ სტანდარტულ მცირე ზომას CV-ს ფოტოსთვის (500x500 პიქსელი)
  const targetSize = 500; 
  canvas.width = targetSize;
  canvas.height = targetSize;
  const ctx = canvas.getContext('2d');
  
  ctx.beginPath();
  ctx.arc(targetSize / 2, targetSize / 2, targetSize / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  
  // ვხატავთ და ვაპატარავებთ სურათს წინასწარ განსაზღვრულ ზომაზე
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, targetSize, targetSize
  );
  
  return canvas.toDataURL('image/png');
};

export default function GeoDocsApp() {
  const [lang, setLang] = useState('GE');
  const [activeTab, setActiveTab] = useState('home');
  const [showNav, setShowNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]); 
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // ფორმის ლოგიკა
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    docLanguage: '', firstName: '', lastName: '', profession: '', personalId: '', email: '',
    birthDate: '', birthPlace: '', phone: '', paymentMethod: '', address: '', serviceType: 'რეზიუმეს დამზადება/ CV',
    consent: false, photoUrl: null, isEmployed: '', availability: '', education: '', experience: '',
    projects: '', personalSkills: [], otherSkills: '', trainings: '', languages: '', software: '', summary: ''
  });

  // ფოტოს მოსაჭრელი ლოგიკა
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setImageSrc(reader.result));
      reader.readAsDataURL(e.target.files[0]);
      setShowCropper(true);
    }
  };

  const handleCropSave = async () => {
    try {
      const croppedImageBase64 = await getCroppedImg(imageSrc, croppedAreaPixels);
      setFormData(prev => ({ ...prev, photoUrl: croppedImageBase64 }));
      setShowCropper(false);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const savedLang = localStorage.getItem('geoDocsLang');
    if (savedLang) setLang(savedLang);
  }, []);

  useEffect(() => {
    localStorage.setItem('geoDocsLang', lang);
  }, [lang]);

  const logoUrl = "/Screenshot_20260326_020239_Facebook.jpg";
  const robotUrl = "/robot.png";

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setShowNav(currentScrollY <= lastScrollY || currentScrollY < 50);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleChatSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim()) return;
    const userMsg = { role: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    const currentInput = input;
    setInput('');
    setLoading(true);
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: currentInput }) 
      });
      const data = await response.json();
      if (data?.candidates?.[0]?.content?.parts) {
        setMessages(prev => [...prev, { role: 'bot', text: data.candidates[0].content.parts[0].text }]);
      } else {
        setMessages(prev => [...prev, { role: 'bot', text: "ბოდიში, პასუხი ვერ მივიღე. სცადე თავიდან." }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: 'bot', text: "ბოდიში, დროებით კავშირის პრობლემაა. გთხოვთ სცადოთ ხელახლა." }]);
    }
    setLoading(false);
  };

  const countriesList = [
    "🇦🇺 ავსტრალია (Australia)", "🇦🇹 ავსტრია (Austria)", "🇦🇿 აზერბაიჯანი (Azerbaijan)", "🇺🇸 ამერიკის შეერთებული შტატები (USA)",
    "🇦🇪 არაბული (UAE)", "🇧🇾 ბელარუსი (Belarus)", "🇧🇪 ბელგია (Belgium)", "🇧🇷 ბრაზილია (Brazil)",
    "🇧🇬 ბულგარეთი (Bulgaria)", "🇩🇪 გერმანია (Germany)", "🇩🇰 დანია (Denmark)", "🇪🇸 ესპანეთი (Spain)",
    "🇪🇪 ესტონეთი (Estonia)", "🇹🇷 თურქეთი (Turkey)", "🇯🇵 იაპონური (Japan)", "🇬🇧 ინგლისი (Great Britain)",
    "🇮🇳 ინდური (India)", "🇮🇪 ირლანდია (Ireland)", "🇮🇱 ისრაელი (Israel)", "🇮🇹 იტალია (Italy)",
    "🇨🇾 კვიპროსი (Cyprus)", "🇰🇷 კორეული (Korea)", "🇱🇹 ლიტვა (Lithuania)", "🇲🇽 მექსიკა (Mexico)",
    "🇳🇴 ნორვეგია (Norway)", "🇵🇱 პოლონეთი (Poland)", "🇵🇹 პორტუგალია (Portugal)", "🇷🇴 რუმინეთი (Romania)",
    "🇷🇺 რუსეთი (Russia)", "🇬🇷 საბერძნეთი (Greece)", "🇬🇪 საქართველო (Georgia)", "🇦🇲 სომხეთი (Armenia)",
    "🇺🇦 უკრაინა (Ukraine)", "🇭🇺 უნგრეთი (Hungary)", "🇫🇮 ფინეთი (Finland)", "🇫🇷 საფრანგეთი (France)",
    "🇨🇳 ჩინეთი (China)", "🇨🇿 ჩეხეთი (Czech Republic)", "🇸🇪 შვედეთი (Sweden)"
  ];

  const personalSkillsList = [
    "კომუნიკაბელური", "გუნდური მუშაობა", "პუნქტუალურობა", "სტრესულ გარემოში მუშაობა",
    "დეტალებზე ორიენტირებულობა", "კრიტიკული აზროვნება", "პასუხისმგებლობის მაღალი გრძნობა",
    "სწრაფი ათვისების უნარი", "დროის მართვა", "პრობლემის გადაჭრის უნარი", "ანალიტიკური აზროვნება"
  ];

  const handleSkillToggle = (skill) => {
    setFormData(prev => {
      const skills = prev.personalSkills.includes(skill)
        ? prev.personalSkills.filter(s => s !== skill)
        : [...prev.personalSkills, skill];
      return { ...prev, personalSkills: skills };
    });
  };

  const handlePaymentAndSubmit = async () => {
    if (!formData.docLanguage || !formData.firstName || !formData.lastName || !formData.email) {
      alert("გთხოვთ შეავსოთ სავალდებულო ველები (ენა, სახელი, გვარი, მეილი)!");
      return;
    }

    setIsProcessingPayment(true);

    try {
      const amount = formData.docLanguage.includes('საქართველო') ? 10 : 15; 
      
      const response = await fetch('/api/initiate-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount,
          email: formData.email,
          currency: 'GEL',
          description: `CV Generation - ${formData.firstName} ${formData.lastName}`
        })
      });
      
      const textData = await response.text();
      
      if (!response.ok) {
        throw new Error(`სერვერის შეცდომა (სტატუსი ${response.status}): ${textData}`);
      }
      
      const paymentData = JSON.parse(textData);
      
      const finalUrl = paymentData.paymentUrl || paymentData.checkout_url || (paymentData.response && paymentData.response.checkout_url);
      
      if (finalUrl) {
        localStorage.setItem('pendingCvData', JSON.stringify(formData));
        window.location.href = finalUrl;
      } else {
         throw new Error(`ბანკის ლინკი ვერ მოიძებნა. მოვიდა ეს მონაცემი: ${textData}`);
      }

    } catch (error) {
      console.error("შეცდომა:", error);
      alert("შეცდომის დეტალები ეკრანზე: " + error.message);
      setIsProcessingPayment(false); 
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment_status'); 
    
    if (paymentStatus === 'success') {
      const pendingData = localStorage.getItem('pendingCvData');
      if (pendingData) {
        const parsedData = JSON.parse(pendingData);
        sendToGoogleSheet(parsedData);
      }
    } else if (paymentStatus === 'failed') {
        alert('გადახდა ვერ განხორციელდა. გთხოვთ სცადოთ თავიდან.');
        localStorage.removeItem('pendingCvData');
    }
  }, []);

  // --- ჩასწორებული ფუნქცია ---
  const sendToGoogleSheet = async (data) => {
    try {
        const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbyNIuGTTN4sWwMsgtcKrU6raFY5lJYcDLgKaMIDApbj8g0IIlPMZJuXIMoaIYo5i2fd/exec"; 

        alert("გადახდა წარმატებულია! მიმდინარეობს მონაცემების დამუშავება და CV-ს გენერირება. გთხოვთ დაელოდოთ (დაახლოებით 1 წუთი).");
        
        localStorage.removeItem('pendingCvData');
        window.history.replaceState({}, document.title, window.location.pathname);
        setActiveTab('home');
        setFormStep(1);

        fetch(SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(data)
        });

    } catch (error) {
        console.error("შეცდომა გაგზავნისას:", error);
        alert("გადახდა შესრულდა, მაგრამ დაფიქსირდა შეცდომა სისტემასთან დაკავშირებისას. გთხოვთ, დაგვიკავშირდეთ.");
    }
  }

  const t = {
    GE: { 
      sloganPart1: "ნუ მოწყდები ", sloganPart2: "შენს საქმეს", alert: "პერსონალური მონაცემები იშლება 5 წუთში!",
      cvBtn: "AI CV 2 წუთში, ნებისმიერ ენაზე", invoiceBtn: "ინვოისი (მალე)", pricesTitle: "ტარიფები",
      prices: [{ title: "სივის ქართულად გენერირება", price: "10₾" }, { title: "სივის უცხო ენაზე გენერირება", price: "15₾" }], 
      damisoBadge: "✨ Dr. Damiso — შენი AI CV ოსტატი", aboutTitle: "ჩვენს შესახებ", 
      aboutContent: (
        <div style={{ lineHeight: '1.6', fontSize: '15px' }}>
          <p style={{ marginBottom: '15px' }}>ჯეო დოკს სერვისი არის პირველი ქართული სრულად ავტომატიზებული პლატფორმა. ჩვენი გუნდი მუშაობს მაღალი სიზუსტის ხელოვნურ ინტელექტზე, რათა თქვენი დოკუმენტები მომზადდეს წამებში.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>ფუნქციონალი და დანიშნულება</p>
          <p style={{ marginBottom: '10px' }}>პლატფორმა ორიენტირებულია ბიზნესპროცესების გასამარტივებლად და დოკუმენტბრუნვის სრული ავტომატიზაციისთვის. მისი მეშვეობით შეგიძლიათ მარტივად დააგენერიროთ:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '15px' }}>
            <li><b style={{ color: '#FFB800' }}>ავტომატიზებული CV-ები (რეზიუმეები):</b> საერთაშორისო სტანდარტების შესაბამისად, ნებისმიერ ენაზე.</li>
            <li><b style={{ color: '#FFB800' }}>ინვოისები და შაბლონები:</b> ფინანსური და ადმინისტრაციული დოკუმენტების სწრაფი მართვა.</li>
          </ul>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>მთავარი მისია ემიგრანტებისთვის</p>
          <p style={{ marginBottom: '15px' }}>ჩვენი პლატფორმა სპეციალურად შეიქმნა ქართველი ემიგრანტებისთვის. ის გაძლევთ უნიკალურ შესაძლებლობას, კითხვარი შეავსოთ მარტივად, მშობლიურ (ქართულ) ენაზე, ხოლო უმაღლესი ხარისხის პროფესიონალური დოკუმენტი 2 წუთში დაგენერირდება და გამოგეგზავნებათ ზუსტად იმ ქვეყნის ენაზე, სადაც იმყოფებით.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>ტექნოლოგიური უპირატესობა</p>
          <p style={{ marginBottom: '15px' }}>ჩვენი სისტემა ეფუძნება თანამედროვე ვებ-დეველოპმენტის უახლეს სტანდარტებსა და საიმედო ღრუბლოვან (Cloud) ინფრასტრუქტურას, რაც უზრუნველყოფს პლატფორმის შეუფერხებელ, სწრაფ მუშაობასა და მონაცემთა მაქსიმალურ დაცვას.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>თქვენი პერსონალური AI ასისტენტი</p>
          <p>გაიცანით დოქტორი დამისო — ჩვენი პლატფორმის გული და CV-ების მთავარი ოსტატი! ის არის მაღალი სიზუსტის ხელოვნური ინტელექტი, რომელიც თქვენს გამოცდილებას აანალიზებს და საერთაშორისო სტანდარტების რეზიუმედ აქცევს. დამისო 24/7-ზე თქვენს განკარგულებაშია.</p>
        </div>
      ),
      rights: "© 2026 GEO DOCS SERVICE. ყველა უფლება დაცულია.", termsTitle: "წესები და პირობები", refundTitle: "გადახდა, გაუქმება და თანხის დაბრუნება",
      refundContent1: "სერვისის სპეციფიკიდან გამომდინარე (ციფრული პროდუქტის მყისიერი მიწოდება), მომხმარებლის მიერ სერვისის საფასურის გადახდის და დოკუმენტის (CV) ავტომატური გენერირების შემდეგ, გადახდილი თანხა უკან არ ბრუნდება.",
      refundContent2: "შეკვეთის გაუქმება შესაძლებელია მხოლოდ სერვისის საფასურის გადახდის პროცესის დასრულებამდე.",
      footerAddress: "მისამართი: ქ. რუსთავი, დ. კლდიაშვილის 12", footerEmail: "ელ-ფოსტა: info@geodocsservice.ge", footerPhone: "ტელ: +995 511 26 86 99",
      termsLink: "წესები და პირობები", termsContent: "პლატფორმა Geo Docs Service-ს (იურიდიული პირი: ი.მ. მანანა შალვაშვილი, 01005042105) მუშაობის პრინციპები და უსაფრთხოების წესები:"
    },
    EN: { 
      sloganPart1: "Stay Focused on ", sloganPart2: "Your Business", alert: "Personal data is deleted in 5 mins!",
      cvBtn: "AI CV in 2 Mins, Any Language", invoiceBtn: "Invoice (Soon)", pricesTitle: "Pricing",
      prices: [{ title: "CV Generation in Georgian", price: "10₾" }, { title: "CV Generation in Foreign Language", price: "15₾" }], 
      damisoBadge: "✨ Dr. Damiso — Your AI CV Master", aboutTitle: "About Us", 
      aboutContent: (
        <div style={{ lineHeight: '1.6', fontSize: '15px' }}>
          <p style={{ marginBottom: '15px' }}>Geo Docs Service is the first fully automated Georgian platform. Our team works on high-precision artificial intelligence so that your documents are prepared in seconds.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>Functionality and Purpose</p>
          <p style={{ marginBottom: '10px' }}>The platform is focused on simplifying business processes and fully automating document flow. With our service, you can easily generate:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '15px' }}>
            <li><b style={{ color: '#FFB800' }}>Automated CVs (Resumes):</b> In accordance with international standards, in any language.</li>
            <li><b style={{ color: '#FFB800' }}>Invoices and Templates:</b> Fast management of financial and administrative documents.</li>
          </ul>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>Main Mission for Emigrants</p>
          <p style={{ marginBottom: '15px' }}>Our platform was specifically created for Georgian emigrants. It gives you the unique opportunity to easily fill out the questionnaire in your native (Georgian) language, while a high-quality professional document is generated in 2 minutes and sent to you in the exact language of your host country.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>Technological Advantage</p>
          <p style={{ marginBottom: '15px' }}>Our system is based on the latest standards of modern web development and reliable Cloud infrastructure, ensuring uninterrupted, fast operation and maximum data protection.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>Your Personal AI Assistant</p>
          <p>Meet Dr. Damiso — the heart of our platform and the master of CVs! He is a high-precision artificial intelligence that analyzes your experience and turns it into a resume of international standards. Damiso is at your disposal 24/7.</p>
        </div>
      ),
      rights: "© 2026 GEO DOCS SERVICE. All rights reserved.", termsTitle: "Terms and Conditions", refundTitle: "Payment, Cancellation, and Refund Policy",
      refundContent1: "Due to the nature of the service (instant delivery of a digital product), the paid amount is non-refundable once the user has paid the service fee and the document (CV) has been automatically generated.",
      refundContent2: "Order cancellation is only possible before the payment process is completed.",
      footerAddress: "Address: 12 D. Kldiashvili St., Rustavi", footerEmail: "Email: info@geodocsservice.ge", footerPhone: "Tel/Тел: +995 511 26 86 99",
      termsLink: "Terms and Conditions", termsContent: "Operating rules and security guidelines for Geo Docs Service (Legal Entity: I.E. Manana Shalvashvili, 01005042105):"
    },
    RU: { 
      sloganPart1: "Не отвлекайтесь ", sloganPart2: "от дел", alert: "Личные данные удаляются через 5 мин!",
      cvBtn: "AI CV за 2 минуты, на любом языке", invoiceBtn: "Инвойс (Скоро)", pricesTitle: "Тарифы",
      prices: [{ title: "Генерация резюме на грузинском", price: "10₾" }, { title: "Генерация резюме на иностранном языке", price: "15₾" }], 
      damisoBadge: "✨ Dr. Damiso — Ваш AI Мастер Резюме", aboutTitle: "О нас", 
      aboutContent: (
        <div style={{ lineHeight: '1.6', fontSize: '15px' }}>
          <p style={{ marginBottom: '15px' }}>Geo Docs Service — первая полностью автоматизированная грузинская платформа. Наша команда работает над высокоточным искусственным интеллектом, чтобы ваши документы были готовы за считанные секунды.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>Функциональность и Назначение</p>
          <p style={{ marginBottom: '10px' }}>Платформа ориентирована на упрощение бизнес-процессов и полную автоматизацию документооборота. С нашей помощью вы можете легко генерировать:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '15px' }}>
            <li><b style={{ color: '#FFB800' }}>Автоматизированные резюме (CV):</b> В соответствии с международными стандартами, на любом языке.</li>
            <li><b style={{ color: '#FFB800' }}>Инвойсы и шаблоны:</b> Быстрое управление финансовыми и административными документами.</li>
          </ul>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>Главная миссия для эмигрантов</p>
          <p style={{ marginBottom: '15px' }}>Наша платформа была специально создана для грузинских эмигрантов. Она дает вам уникальную возможность легко заполнить анкету на родном (грузинском) языке, а высококачественный профессиональный документ будет сгенерирован за 2 минуты и отправлен вам именно на языке страны вашего пребывания.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>Технологическое Преимущество</p>
          <p style={{ marginBottom: '15px' }}>Наша система основана на новейших стандартах современной веб-разработки и надежной облачной (Cloud) инфраструктуре, что обеспечивает бесперебойную, быструю работу и максимальную защиту данных.</p>
          <p style={{ color: '#FFB800', fontWeight: 'bold', marginBottom: '10px' }}>Ваш персональный AI ассистент</p>
          <p>Познакомьтесь с доктором Дамисо — сердцем нашей платформы и мастером резюме! Это высокоточный искусственный интеллект, который анализирует ваш опыт и превращает его в резюме по международным стандартам. Дамисо в вашем распоряжении 24/7.</p>
        </div>
      ),
      rights: "© 2026 GEO DOCS SERVICE. Все права защищены.", termsTitle: "Правила и условия", refundTitle: "Оплата, отмена и возврат средств",
      refundContent1: "В связи со спецификой сервиса (мгновенная доставка цифрового продукта), уплаченная сумма возврату не подлежит после того, как пользователь оплатил стоимость услуги и документ (CV) был автоматически сгенерирован.",
      refundContent2: "Отмена заказа возможна только до завершения процесса оплаты.",
      footerAddress: "Адрес: г. Рустави, ул. Д. Клдиашвили 12", footerEmail: "Email: info@geodocsservice.ge", footerPhone: "Tel/Тел: +995 511 26 86 99",
      termsLink: "Правила и условия", termsContent: "Правила работы и политика безопасности платформы Geo Docs Service (Юридическое лицо: И.П. Манана Шалвашвили, 01005042105):"
    }
  }[lang];

  const inputStyle = { width: '100%', padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#333', color: 'white', marginBottom: '15px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '5px', fontSize: '13px', color: '#FFB800' };

  return (
    <div style={{ backgroundColor: '#6D757D', minHeight: '100vh', color: 'white', fontFamily: 'Arial, sans-serif', width: '100%', overflowX: 'hidden' }}>
      
      <style>{`@keyframes blink { 0% { opacity: 0.2; } 20% { opacity: 1; } 100% { opacity: 0.2; } }`}</style>

      {/* ფოტოს მოსაჭრელი პოპ-აპი */}
      {showCropper && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 10001, display: 'flex', flexDirection: 'column' }}>
          <div style={{ position: 'relative', width: '100%', height: '80%' }}>
            <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false} onCropChange={setCrop} onCropComplete={onCropComplete} onZoomChange={setZoom} />
          </div>
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', backgroundColor: '#1A1A1A', gap: '10px' }}>
            <button onClick={() => setShowCropper(false)} style={{ flex: 1, padding: '15px', borderRadius: '10px', background: '#333', color: 'white', border: 'none' }}>გაუქმება</button>
            <button onClick={handleCropSave} style={{ flex: 1, padding: '15px', borderRadius: '10px', background: '#007AFF', color: 'white', border: 'none', fontWeight: 'bold' }}>მოჭრა და შენახვა</button>
          </div>
        </div>
      )}

      {/* Header */}
      <header style={{ backgroundColor: '#1A1A1A', padding: '15px', position: 'fixed', top: 0, width: '100%', zIndex: 1000, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => {setActiveTab('home'); window.scrollTo(0,0);}}>
            <img src={logoUrl} alt="Logo" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover' }} />
            <div style={{ fontWeight: '900', fontStyle: 'italic' }}>GEO DOCS SERVICE</div>
        </div>
        <div style={{ backgroundColor: '#000', borderRadius: '8px', padding: '2px' }}>
          {['GE', 'EN', 'RU'].map(l => (
            <button key={l} onClick={() => setLang(l)} style={{ backgroundColor: lang === l ? '#007AFF' : 'transparent', color: 'white', border: 'none', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>{l}</button>
          ))}
        </div>
      </header>

      <main style={{ padding: '80px 20px 100px 20px', boxSizing: 'border-box' }}>
        
        {/* Home Tab */}
        {activeTab === 'home' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '15px', fontSize: '11px', marginBottom: '20px' }}>{t.alert}</div>
            <h1 style={{ fontSize: '26px', margin: '20px 0' }}><span style={{ color: 'white' }}>{t.sloganPart1}</span><span style={{ color: '#FFB800' }}>{t.sloganPart2}</span></h1>
            
            <div style={{ position: 'relative', display: 'inline-block', margin: '10px auto', marginBottom: '30px' }}>
              <img src={robotUrl} alt="Dr. Damiso" style={{ width: '220px' }} />
              <div style={{ position: 'absolute', bottom: '-15px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(26, 26, 26, 0.95)', color: '#FFB800', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', border: '1px solid #007AFF', boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>
                {t.damisoBadge}
              </div>
            </div>
            
            <button onClick={() => { setActiveTab('form'); setFormStep(1); window.scrollTo(0,0); }} style={{ width: '100%', padding: '18px', background: '#FFB800', border: 'none', borderRadius: '20px', fontWeight: 'bold', cursor: 'pointer', color: 'black' }}>
              {t.cvBtn}
            </button>
            <button style={{ width: '100%', padding: '15px', background: '#007AFF', border: '2px dashed #FFB800', marginTop: '15px', borderRadius: '20px', color: 'white', fontWeight: 'bold' }}>{t.invoiceBtn}</button>

            <div id="pricing-section" style={{ marginTop: '40px' }}>
              <h2 style={{ textAlign: 'left', marginBottom: '20px', color: '#FFB800' }}>{t.pricesTitle}</h2>
              {t.prices.map((p, i) => (
                <button key={i} onClick={() => { setActiveTab('form'); setFormStep(1); window.scrollTo(0,0); }} style={{ width: '100%', background: '#2A2A2A', padding: '20px', borderRadius: '20px', border: '1px solid #444', marginBottom: '10px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                  <span style={{ fontSize: '14px' }}>{p.title}</span> <b style={{ color: '#FFB800', fontSize: '18px' }}>{p.price}</b>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* --- FORM TAB --- */}
        {activeTab === 'form' && (
          <div style={{ background: '#2A2A2A', padding: '20px', borderRadius: '20px', marginTop: '10px' }}>
            <h2 style={{ color: '#FFB800', marginBottom: '20px', textAlign: 'center' }}>Geo Docs Service - ფორმა</h2>
            
            <div style={{ display: 'flex', gap: '5px', marginBottom: '30px' }}>
              {[1, 2, 3].map(step => (
                <div key={step} style={{ flex: 1, height: '4px', backgroundColor: step <= formStep ? '#FFB800' : '#444', borderRadius: '2px' }} />
              ))}
            </div>

            {formStep === 1 && (
              <div>
                <h3 style={{ marginBottom: '15px' }}>ნაბიჯი 1: ენის არჩევა</h3>
                <label style={labelStyle}>რომელ ენაზე გსურთ დოკუმენტი?</label>
                <select style={inputStyle} value={formData.docLanguage} onChange={e => setFormData({...formData, docLanguage: e.target.value})}>
                  <option value="">აირჩიეთ ქვეყანა...</option>
                  {countriesList.map((country, idx) => (
                    <option key={idx} value={country}>{country}</option>
                  ))}
                </select>
                <button onClick={() => { setFormStep(2); window.scrollTo(0, 0); }} style={{ width: '100%', padding: '15px', background: '#007AFF', color: 'white', border: 'none', borderRadius: '10px', display: 'flex', justifyContent: 'center', gap: '10px' }}>შემდეგი <ChevronRight size={20}/></button>
              </div>
            )}

            {formStep === 2 && (
              <div>
                <h3 style={{ marginBottom: '15px' }}>ნაბიჯი 2: პერსონალური ინფორმაცია</h3>
                <label style={labelStyle}>სახელი</label>
                <input style={inputStyle} type="text" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} />
                
                <label style={labelStyle}>გვარი</label>
                <input style={inputStyle} type="text" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} />
                
                <label style={labelStyle}>პროფესია</label>
                <input style={inputStyle} type="text" value={formData.profession} onChange={e => setFormData({...formData, profession: e.target.value})} />
                
                <label style={labelStyle}>პირადი ნომერი</label>
                <input style={inputStyle} type="text" value={formData.personalId} onChange={e => setFormData({...formData, personalId: e.target.value})} />
                
                <label style={labelStyle}>იმეილი (ლათინური, პატარა ასოებით)</label>
                <input style={inputStyle} type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                
                <label style={labelStyle}>დაბადების თარიღი (დღე/თვე/წელი)</label>
                <input style={inputStyle} type="text" placeholder="მაგ: 15/08/1990" value={formData.birthDate} onChange={e => setFormData({...formData, birthDate: e.target.value})} />
                
                <label style={labelStyle}>დაბადების ადგილი (ქვეყანა, ქალაქი)</label>
                <input style={inputStyle} type="text" value={formData.birthPlace} onChange={e => setFormData({...formData, birthPlace: e.target.value})} />
                
                <label style={labelStyle}>ტელეფონის ნომერი</label>
                <input style={inputStyle} type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                
                <label style={labelStyle}>ფაქტობრივი საცხოვრებელი მისამართი</label>
                <textarea style={{...inputStyle, height: '80px'}} value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
                
                <label style={labelStyle}>გადახდის მეთოდი</label>
                <select style={inputStyle} value={formData.paymentMethod} onChange={e => setFormData({...formData, paymentMethod: e.target.value})}>
                  <option value="">აირჩიეთ...</option><option value="ბარათით გადახდა">ბარათით გადახდა</option>
                </select>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button onClick={() => { setFormStep(1); window.scrollTo(0, 0); }} style={{ flex: 1, padding: '15px', background: '#444', color: 'white', border: 'none', borderRadius: '10px' }}><ChevronLeft size={20}/></button>
                  <button onClick={() => { setFormStep(3); window.scrollTo(0, 0); }} style={{ flex: 3, padding: '15px', background: '#007AFF', color: 'white', border: 'none', borderRadius: '10px', display: 'flex', justifyContent: 'center', gap: '10px' }}>შემდეგი <ChevronRight size={20}/></button>
                </div>
              </div>
            )}

            {formStep === 3 && (
              <div>
                <h3 style={{ marginBottom: '15px' }}>ნაბიჯი 3: CV-ს დეტალები</h3>
                
                <label style={labelStyle}>ატვირთეთ თქვენი ფოტო (მოიჭრება მრგვლად)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                  <label style={{ background: '#007AFF', padding: '12px 20px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold' }}>
                    <Upload size={18} /> არჩევა
                    <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                  </label>
                  {formData.photoUrl && <img src={formData.photoUrl} alt="Cropped" style={{ width: '50px', height: '50px', borderRadius: '50%', border: '2px solid #FFB800' }} />}
                </div>

                <label style={labelStyle}>ამჟამად ხართ თუ არა დასაქმებული?</label>
                <select style={inputStyle} value={formData.isEmployed} onChange={e => setFormData({...formData, isEmployed: e.target.value})}>
                  <option value="">აირჩიეთ...</option><option value="კი">კი</option><option value="არა">არა</option>
                </select>

                <label style={labelStyle}>რამდენ ხანში შეძლებთ მუშაობის დაწყებას?</label>
                <select style={inputStyle} value={formData.availability} onChange={e => setFormData({...formData, availability: e.target.value})}>
                  <option value="">აირჩიეთ...</option><option value="დაუყოვნებლივ">დაუყოვნებლივ</option><option value="2 კვირა">2 კვირა</option><option value="1 თვე">1 თვე</option><option value="3 თვე">3 თვე</option>
                </select>

                <label style={labelStyle}>განათლება (უნივერსიტეტი, ფაკულტეტი, წლები)</label>
                <input style={inputStyle} type="text" value={formData.education} onChange={e => setFormData({...formData, education: e.target.value})} />
                
                <label style={labelStyle}>სამუშაო გამოცდილება</label>
                <textarea style={{...inputStyle, height: '80px'}} value={formData.experience} onChange={e => setFormData({...formData, experience: e.target.value})} />
                
                <label style={labelStyle}>პროექტები</label>
                <textarea style={{...inputStyle, height: '60px'}} value={formData.projects} onChange={e => setFormData({...formData, projects: e.target.value})} />

                <label style={{...labelStyle, marginTop: '10px'}}>პიროვნული უნარები (მონიშნეთ)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px', background: '#333', padding: '15px', borderRadius: '10px' }}>
                  {personalSkillsList.map(skill => (
                    <label key={skill} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer' }}>
                      <div style={{ width: '18px', height: '18px', border: '2px solid #FFB800', borderRadius: '4px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: formData.personalSkills.includes(skill) ? '#FFB800' : 'transparent' }}>
                        {formData.personalSkills.includes(skill) && <Check size={14} color="black" />}
                      </div>
                      {skill}
                      <input type="checkbox" style={{ display: 'none' }} checked={formData.personalSkills.includes(skill)} onChange={() => handleSkillToggle(skill)} />
                    </label>
                  ))}
                </div>

                <label style={labelStyle}>მიუთითეთ სხვა უნარები</label>
                <input style={inputStyle} type="text" value={formData.otherSkills} onChange={e => setFormData({...formData, otherSkills: e.target.value})} />
                
                <label style={labelStyle}>ტრენინგები და სერთიფიკატები</label>
                <input style={inputStyle} type="text" value={formData.trainings} onChange={e => setFormData({...formData, trainings: e.target.value})} />
                
                <label style={labelStyle}>უცხო ენები</label>
                <input style={inputStyle} type="text" value={formData.languages} onChange={e => setFormData({...formData, languages: e.target.value})} />
                
                <label style={labelStyle}>კომპიუტერული პროგრამები</label>
                <input style={inputStyle} type="text" value={formData.software} onChange={e => setFormData({...formData, software: e.target.value})} />
                
                <label style={labelStyle}>პროფესიული შეჯამება (არასავალდებულო)</label>
                <textarea style={{...inputStyle, height: '80px'}} value={formData.summary} onChange={e => setFormData({...formData, summary: e.target.value})} />

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '11px', color: '#ccc', marginBottom: '20px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.consent} onChange={e => setFormData({...formData, consent: e.target.checked})} style={{ marginTop: '2px' }} />
                  თანხმობა პერსონალური მონაცემების დამუშავებაზე. მოწოდებული ინფორმაცია გამოყენებული იქნება ექსკლუზიურად დოკუმენტის მოსამზადებლად და წაიშლება 5 წუთში.
                </label>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => { setFormStep(2); window.scrollTo(0, 0); }} style={{ flex: 1, padding: '15px', background: '#444', color: 'white', border: 'none', borderRadius: '10px' }}><ChevronLeft size={20}/></button>
                  <button 
                    onClick={handlePaymentAndSubmit} 
                    disabled={!formData.consent || isProcessingPayment} 
                    style={{ 
                      flex: 3, 
                      padding: '15px', 
                      background: formData.consent ? (isProcessingPayment ? '#6c757d' : '#28A745') : '#555', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '10px', 
                      fontWeight: 'bold' 
                    }}
                  >
                    {isProcessingPayment ? "მიმდინარეობს..." : "გადახდა და გაგზავნა"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div style={{ background: '#2A2A2A', padding: '20px', borderRadius: '20px', marginTop: '20px' }}>
            <h2 style={{ color: '#FFB800', marginBottom: '15px' }}>{t.aboutTitle}</h2>
            {t.aboutContent}
          </div>
        )}

        {/* Terms and Conditions Tab */}
        {activeTab === 'terms' && (
          <div style={{ background: '#2A2A2A', padding: '20px', borderRadius: '20px', marginTop: '20px' }}>
            <h2 style={{ color: '#FFB800', marginBottom: '15px' }}>{t.termsTitle}</h2>
            <p style={{ fontWeight: 'bold', marginBottom: '15px' }}>{t.termsContent}</p>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
              <li><b style={{ color: '#FFB800' }}>{lang === 'GE' ? 'ტარიფები:' : lang === 'EN' ? 'Pricing:' : 'Тарифы:'}</b> {lang === 'GE' ? 'CV ქართულად - 10₾, უცხო ენაზე - 15₾.' : lang === 'EN' ? 'CV in Georgian - 10₾, Foreign language - 15₾.' : 'Резюме на грузинском - 10₾, на иностранном - 15₾.'}</li>
              <li><b style={{ color: '#FFB800' }}>{lang === 'GE' ? 'მონაცემთა დაცვა:' : lang === 'EN' ? 'Data Protection:' : 'Защита данных:'}</b> {lang === 'GE' ? 'ყველა პერსონალური მონაცემი ავტომატურად იშლება ბაზიდან საბუთის დამზადებიდან 5 წუთის შემდეგ.' : lang === 'EN' ? 'All personal data is automatically deleted from the database 5 minutes after document preparation.' : 'Все личные данные автоматически удаляются из базы через 5 минут после подготовки документа.'}</li>
              <li><b style={{ color: '#FFB800' }}>{lang === 'GE' ? 'მონაცემთა გამოყენება:' : lang === 'EN' ? 'Data Usage:' : 'Использование данных:'}</b> {lang === 'GE' ? 'თქვენგან მოწოდებული ინფორმაცია გამოყენებული იქნება ექსკლუზიურად მხოლოდ თქვენს მიერ შერჩეული დოკუმენტის მოსამზადებლად.' : lang === 'EN' ? 'The information provided by you will be used exclusively for preparing your selected document.' : 'Предоставленная вами информация будет использоваться исключительно для подготовки выбранного вами документа.'}</li>
              <li><b style={{ color: '#FFB800' }}>{lang === 'GE' ? 'მომსახურების უზრუნველყოფა:' : lang === 'EN' ? 'Service Provision:' : 'Обеспечение обслуживания:'}</b> {lang === 'GE' ? 'სისტემაში უსაფრთხოების მიზნით ინახება მხოლოდ შეკვეთის თარიღი, არჩეული ენა და თქვენი ელ-ფოსტა, რომლებიც ასევე წაიშლება ყოველ ღამის 12 საათზე.' : lang === 'EN' ? 'For security reasons, only the order date, chosen language, and your email are stored, which are also deleted every night at 12:00 AM.' : 'В целях безопасности сохраняются только дата заказа, выбранный язык и ваша электронная почта, которые также удаляются каждую ночь в 12 часов.'}</li>
              <li><b style={{ color: '#FFB800' }}>{lang === 'GE' ? 'ჩასწორება / განმეორებითი დოკუმენტი:' : lang === 'EN' ? 'Correction / Repeat Document:' : 'Исправление / Повторный документ:'}</b> {lang === 'GE' ? 'თუ დოკუმენტში შეცდომაა, გთხოვთ, კითხვარი შეავსოთ თავიდან. სისტემა ამოწმებს შენახულ სამ მონაცემს (თარიღი, ენა, ელ-ფოსტა). დამთხვევის შემთხვევაში, სისტემა ავტომატურად მოგცემთ კორექტირების საშუალებას და ახალი, ჩასწორებული დოკუმენტი სრულიად უფასოდ დაგიმზადდებათ.' : lang === 'EN' ? 'If there is an error in the document, please fill out the questionnaire again. The system checks the three stored data points (date, language, email). If they match, the system will automatically allow correction and a new, corrected document will be generated completely free of charge.' : 'Если в документе есть ошибка, пожалуйста, заполните анкету заново. Система проверяет три сохраненных параметра (дата, язык, email). При совпадении система автоматически разрешит корректировку и бесплатно создаст новый, исправленный документ.'}</li>
              <li><b style={{ color: '#FFB800' }}>{lang === 'GE' ? 'ფაილების შენახვა:' : lang === 'EN' ? 'File Storage:' : 'Хранение файлов:'}</b> {lang === 'GE' ? 'გენერირებული PDF დოკუმენტები PDF Drive-იდან იშლება ყოველ ღამის 12 საათზე.' : lang === 'EN' ? 'Generated PDF documents are deleted from PDF Drive every night at 12:00 AM.' : 'Сгенерированные PDF-документы удаляются из PDF Drive каждую ночь в 12 часов.'}</li>
            
              <h3 style={{ color: '#FFB800', marginTop: '20px', marginBottom: '10px', fontSize: '18px' }}>{t.refundTitle}</h3>
              <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
                <li>{t.refundContent1}</li>
                <li>{t.refundContent2}</li>
              </ul>
            </ul>
          </div>
        )}

        {/* Footer */}
        <footer style={{ textAlign: 'center', marginTop: '40px', opacity: 0.6 }}>
          <img src={logoUrl} alt="Logo" style={{ width: '40px', height: '40px', borderRadius: '50%', marginBottom: '10px', objectFit: 'cover' }} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '10px' }}>
            <Instagram size={20} /> <Facebook size={20} /> <Send size={20} />
          </div>
          
          <div onClick={() => { setActiveTab('terms'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ fontSize: '12px', textDecoration: 'underline', cursor: 'pointer', marginBottom: '10px', color: '#FFB800', fontWeight: 'bold' }}>
            {t.termsLink}
          </div>
          
          <div style={{ fontSize: '11px', lineHeight: '1.6', marginBottom: '15px' }}>
            <div style={{ fontWeight: 'bold' }}>Geo Docs Service</div>
            <div>{t.footerAddress}</div>
            <div>{t.footerEmail}</div>
            <div>{t.footerPhone}</div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
            <div style={{ width: '32px', height: '19px', backgroundColor: '#fff', borderRadius: '3px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: '900', color: '#0040FF', fontSize: '10px', fontStyle: 'italic', letterSpacing: '0.5px' }}>VISA</div>
            <div style={{ width: '32px', height: '19px', backgroundColor: '#fff', borderRadius: '3px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <span style={{ width: '10px', height: '10px', backgroundColor: '#FF0000', borderRadius: '50%', display: 'inline-block' }}></span>
              <span style={{ width: '10px', height: '10px', backgroundColor: '#FFB800', borderRadius: '50%', display: 'inline-block', marginLeft: '-4px', opacity: '0.9' }}></span>
            </div>
          </div>
          <p style={{ fontSize: '10px' }}>{t.rights}</p>
        </footer>
      </main>

      {/* Dr. Damiso Chat Window */}
      {isChatOpen && (
        <div style={{ position: 'fixed', bottom: '150px', right: '5%', width: '90%', maxWidth: '350px', height: '400px', backgroundColor: '#1A1A1A', borderRadius: '20px', border: '2px solid #007AFF', zIndex: 10000, display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.6)' }}>
          <div style={{ padding: '12px 15px', backgroundColor: '#007AFF', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}>
            <span style={{ fontWeight: 'bold' }}>Dr. Damiso</span>
            <X size={20} onClick={() => setIsChatOpen(false)} style={{ cursor: 'pointer' }} />
          </div>
          <div style={{ flex: 1, padding: '15px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', backgroundColor: m.role === 'user' ? '#007AFF' : '#333', padding: '10px 14px', borderRadius: '15px', maxWidth: '85%', fontSize: '14px', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>{m.text}</div>
            ))}
            {loading && (
              <div style={{ alignSelf: 'flex-start', backgroundColor: '#333', padding: '12px 16px', borderRadius: '15px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={{width: '6px', height: '6px', backgroundColor: '#fff', borderRadius: '50%', animation: 'blink 1.4s infinite both'}}></span>
                <span style={{width: '6px', height: '6px', backgroundColor: '#fff', borderRadius: '50%', animation: 'blink 1.4s infinite both 0.2s'}}></span>
                <span style={{width: '6px', height: '6px', backgroundColor: '#fff', borderRadius: '50%', animation: 'blink 1.4s infinite both 0.4s'}}></span>
              </div>
            )}
          </div>
          <form onSubmit={handleChatSubmit} style={{ padding: '10px', display: 'flex', gap: '8px', borderTop: '1px solid #333', margin: 0 }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="მოწერე შეტყობინება..." style={{ flex: 1, padding: '10px 12px', borderRadius: '10px', border: 'none', backgroundColor: '#fff', color: '#000', fontSize: '14px', outline: 'none' }} />
            <button type="submit" style={{ background: '#FFB800', border: 'none', borderRadius: '10px', padding: '0 15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Send size={18} color="#000" /></button>
          </form>
        </div>
      )}

      {/* Dr. Damiso Chat Button */}
      <div style={{ position: 'fixed', bottom: '80px', right: '20px', display: 'flex', alignItems: 'center', gap: '10px', zIndex: 9999 }}>
        <div style={{ backgroundColor: '#1A1A1A', color: 'white', padding: '8px 15px', borderRadius: '20px', fontWeight: 'bold', fontSize: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>Dr. Damiso</div>
        <div onClick={() => setIsChatOpen(!isChatOpen)} style={{ backgroundColor: 'white', border: '3px solid #007AFF', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
          <img src={robotUrl} alt="Dr. Damiso" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      </div>
      
      {/* Navigation */}
      <nav style={{ position: 'fixed', bottom: '15px', left: '5%', width: '90%', backgroundColor: '#1A1A1A', padding: '15px', borderRadius: '40px', display: 'flex', justifyContent: 'space-around', zIndex: 1000, boxSizing: 'border-box', transition: 'transform 0.3s ease, opacity 0.3s ease', transform: showNav ? 'translateY(0)' : 'translateY(100px)', opacity: showNav ? 1 : 0, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
        <Home onClick={() => {setActiveTab('home'); window.scrollTo(0,0);}} style={{ color: activeTab === 'home' ? '#007AFF' : 'white', cursor: 'pointer' }} />
        <LayoutGrid onClick={() => { setActiveTab('home'); setTimeout(() => { document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' }); }, 100); }} style={{ cursor: 'pointer', color: 'white' }} />
        <FileText onClick={() => {setActiveTab('terms'); window.scrollTo(0,0);}} style={{ color: activeTab === 'terms' ? '#007AFF' : 'white', cursor: 'pointer' }} />
        <Info onClick={() => {setActiveTab('about'); window.scrollTo(0,0);}} style={{ color: activeTab === 'about' ? '#007AFF' : 'white', cursor: 'pointer' }} />
      </nav>
    </div>
  );
}

